import { AdditionalFlags } from '@kijiji/generated/graphql-types'
import { useTranslation } from 'next-i18next'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { RenderListingCard } from '@/components/srp/search-list/RenderListingCard'
import {
  FullScreenDivider,
  SearchListContainer,
  TopAdsContainer,
} from '@/components/srp/search-list/styled'
import { useSearchActions } from '@/hooks/srp'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type Listing } from '@/types/search'
import { BodyText } from '@/ui/atoms/body-text'
import { Divider } from '@/ui/atoms/divider'
import { Flex } from '@/ui/atoms/flex'
import { Spacing } from '@/ui/atoms/spacing'
import { TextLink } from '@/ui/atoms/text-link'

type RenderTopAdsProps = {
  topAds: Listing[]
  shouldRenderMapSRP?: boolean
}

export const RenderTopAds = ({ topAds, shouldRenderMapSRP }: RenderTopAdsProps) => {
  const { colors, spacing } = useTheme()
  const { t } = useTranslation(['srp', 'common'])

  const { refetchResults } = useSearchActions()

  const handleSeeAllTopAds = () =>
    refetchResults({
      additionalFlagFilters: [AdditionalFlags.TopAd],
      offset: 0,
      topAdCount: 0,
    })

  if (!topAds.length) return null

  const trackTopAdLearnMoreClick = () => {
    trackEvent({ action: GA_EVENT.TopAdLearnMoreClick })
  }

  const TopAdsTitleOriginal = () => (
    <BodyText size="medium" color={colors.grey.primary}>
      {t('srp:listing.top_ad.label')}{' '}
      <LinkCustom
        aria-label={t('srp:listing.top_ad.learn_more_long_label')}
        href={t('srp:listing.top_ad.learn_more_link')}
        hasUnderline
        onClick={trackTopAdLearnMoreClick}
        variant="secondary"
      >
        {t('common:labels.learn_more')}
      </LinkCustom>
    </BodyText>
  )

  const TopAdsTitleMwebRedesign = () => (
    <Flex justifyContent="space-between">
      <BodyText size="xLarge" weight="semiBold" color={colors.grey.primary}>
        {t('srp:listing.top_ad.mobile.label')}
      </BodyText>

      <TextLink
        as="button"
        hasUnderline
        onClick={handleSeeAllTopAds}
        variant="secondary"
        data-testid="see-all-top-ads"
      >
        {t('common:labels.view_all')}
      </TextLink>
    </Flex>
  )

  return (
    <TopAdsContainer>
      <FullScreenDivider>
        <Divider bottom={spacing.default} size="medium" />
      </FullScreenDivider>

      <Spacing mBottom={spacing.default}>
        <ShowAtOrLarger breakpoint="medium">
          <TopAdsTitleMwebRedesign />
        </ShowAtOrLarger>

        <HideAtOrLarger breakpoint="medium">
          <TopAdsTitleOriginal />
        </HideAtOrLarger>
      </Spacing>

      <SearchListContainer data-testid="top-listings">
        {topAds.map((item, index) => (
          <RenderListingCard
            key={item.id}
            index={index}
            isTopAd
            item={item}
            isMapCard={shouldRenderMapSRP}
          />
        ))}
      </SearchListContainer>

      {!shouldRenderMapSRP && (
        <HideAtOrLarger breakpoint="medium">
          <Spacing mTop={spacing.default}>
            <TextLink
              as="button"
              hasUnderline
              onClick={handleSeeAllTopAds}
              variant="secondary"
              data-testid="see-all-top-ads"
            >
              {t('common:labels.see_all')}
            </TextLink>
          </Spacing>
        </HideAtOrLarger>
      )}

      <FullScreenDivider>
        <Divider top={spacing.default} size="medium" />
      </FullScreenDivider>
    </TopAdsContainer>
  )
}
